import { log } from '@/log'
import { CartScopeEq } from '@/models/cart/group/scope'
import { URL } from '@/util/url'
import { OrderContext } from '@spa/components/pages/order/model/context/order-context'
import { toQuery } from '@spa/components/pages/order/scope-transpiler'
import { ApiContact, ContactService } from '@spa/services/api/contact'
import { convertToRegionName } from '@spa/services/region'
import store from '@spa/store'
import { CartItem } from '@spa/store/modules/cart/types'
import _ from 'lodash'
import { Location } from 'vue-router'

const redirectWithScope = (path: string, scope: CartScopeEq): Location => ({
  path,
  query: {
    scope: toQuery(scope),
    from: URL.extractMaybeSpaPath(location.pathname),
  },
})

export interface ParameterForGuardOnCart {
  scope: CartScopeEq
  redirectLocation: () => Location,
  filter: (item: CartItem) => boolean,
}
export async function guardOnCart({
                                    scope,
                                    redirectLocation,
                                    filter,
}: ParameterForGuardOnCart): Promise<Location | void> {
  log.debug({ guard: 'guardOnCart', scope })
  await store.dispatch('cart/loadWithScope', { scope })

  const error = _.get(store.state.cart.error, 'load')

  if (error) {
    log.error({ error })

    alert('カート情報の取得に失敗しました。\n※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。')

    return redirectLocation()
  }

  const items: CartItem[] = store.getters['cart/displayItemsWithFilter'](filter)

  const itemsCount = _.sumBy(items, item => _.toNumber(item.unitCount))

  if (itemsCount === 0) {
    log.warn('any cart item not found, so redirect to cart page in order to check cart state')

    return redirectLocation()
  }
}

export interface ParameterForGuardOnUser {
  scope: CartScopeEq,
  context: OrderContext
}
export async function guardOnUser({ scope, context }: ParameterForGuardOnUser): Promise<Location | void> {
  log.info('test if user status is fulfilled or not')

  await store.dispatch('user/get', { force: true })

  const user = store.state.user.user

  const errorOnGet = _.get(store.state.user, 'error.get')

  if (errorOnGet) {
    log.error('failed to fetch current user', errorOnGet)

    throw new Error('failed to fetch current user')
  }

  if (!user || user.user_status !== 'registered') {
    log.error('failed to resolve registered user', user)

    throw new Error('registered user not resolved after session check')
  }

  if (!store.getters['user/hasMinimumNecessaryProfileForPurchase']) {
    log.warn(`redirect to complete profile page because user registered but some profile not fulfilled`)

    return redirectWithScope(context.path.completeProfile, scope)
  }

  log.info('user status is fulfilled')
}

export interface ParameterForGuardOnDelivery {
  scope: CartScopeEq
  context: OrderContext
}
export async function guardOnDelivery({ scope, context }: ParameterForGuardOnDelivery): Promise<Location | void> {
  log.info('test if delivery is fulfilled or not')
  const delivery = store.state.orderConfirm.delivery

  if (delivery) {
    log.info('delivery is fulfilled, so any redirect is not necessary')

    return
  }

  // Fallback
  await store.dispatch('contact/load', { force: true })

  if (_.isEmpty(store.state.contact.items)) {
    // Try to copy address from user profile if there is no contact
    try {
      await createContactFromProfile()
    } catch (e) {
      log.warn(
        'failed to create contact from profile because profile maybe not fulfilled, so redirect to complete-profile page',
        e
      )

      return {
        path: '/order/complete-profile',
      }
    }
  }

  if (!_.isEmpty(store.state.contact.items)) {
    log.info('contact records are exist, so any redirect is not necessary')

    return
  }

  log.warn('contact records not exist, so redirect to contact-edit page')

  return redirectWithScope(context.path.editContact, scope)
}

async function createContactFromProfile(): Promise<void> {
  await store.dispatch('user/get')

  const user = store.state.user.user

  if (!user || user.user_status !== 'registered') {
    return
  }

  const userHasAddress = await store.getters['user/hasMinimumNecessaryProfileForPurchase']

  if (!userHasAddress) {
    return
  }

  // Create new contact
  const contactPayload: ApiContact = {
    location_kind: 0,
    postal_code: user.zipcode,
    region: convertToRegionName(user.region),
    region_code: user.region,
    locality: user.address1,
    street_address: user.address2,
    first_name: user.first_name,
    last_name: user.last_name,
    tel: user.tel,
    is_delivery: true,
    display_level: 0,
  }

  const contactResult = await new ContactService().create(contactPayload)

  if (contactResult.isLeft()) {
    alert('ユーザ情報から住所を配送先にコピーするのに失敗しました')

    throw new Error('ユーザ情報から住所を配送先にコピーするのに失敗しました')
  }

  // refresh
  await store.dispatch('contact/load', { force: true })
}
